import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import DashboardHeader from './components/DashboardHeader';
import KPICard from './components/KPICard';
import RevenueChart from './components/RevenueChart';
import NotificationFeed from './components/NotificationFeed';
import TopTenantsTable from './components/TopTenantsTable';

const PlatformOverviewDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [refreshInterval, setRefreshInterval] = useState('5m');

  // Mock KPI data
  const kpiData = [
    {
      title: "Total Companies",
      value: "2,847",
      change: "+12.5%",
      changeType: "positive",
      icon: "Building2",
      trend: [0.2, 0.4, 0.3, 0.8, 0.6, 0.9, 1.0]
    },
    {
      title: "Active Users",
      value: "45,892",
      change: "+8.2%",
      changeType: "positive",
      icon: "Users",
      trend: [0.3, 0.5, 0.7, 0.6, 0.8, 0.9, 1.0]
    },
    {
      title: "Monthly Revenue",
      value: "$847K",
      change: "+15.3%",
      changeType: "positive",
      icon: "DollarSign",
      trend: [0.4, 0.6, 0.5, 0.8, 0.7, 0.9, 1.0]
    },
    {
      title: "System Uptime",
      value: "99.97%",
      change: "+0.02%",
      changeType: "positive",
      icon: "Activity",
      trend: [0.9, 0.95, 0.98, 0.99, 0.97, 0.99, 1.0]
    },
    {
      title: "Processing Volume",
      value: "1.2M",
      change: "+22.1%",
      changeType: "positive",
      icon: "Zap",
      trend: [0.5, 0.6, 0.8, 0.7, 0.9, 0.8, 1.0]
    },
    {
      title: "Churn Rate",
      value: "2.3%",
      change: "-0.8%",
      changeType: "positive",
      icon: "TrendingDown",
      trend: [0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2]
    },
    {
      title: "Support Tickets",
      value: "127",
      change: "-15.2%",
      changeType: "positive",
      icon: "MessageSquare",
      trend: [0.8, 0.6, 0.7, 0.5, 0.4, 0.3, 0.2]
    },
    {
      title: "API Response Time",
      value: "142ms",
      change: "-8.5%",
      changeType: "positive",
      icon: "Clock",
      trend: [0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1]
    }
  ];

  // Mock chart data
  const chartData = [
    { month: 'Jan', revenue: 650, users: 38000, tenants: 2100 },
    { month: 'Feb', revenue: 680, users: 39500, tenants: 2200 },
    { month: 'Mar', revenue: 720, users: 41000, tenants: 2350 },
    { month: 'Apr', revenue: 750, users: 42500, tenants: 2450 },
    { month: 'May', revenue: 780, users: 43800, tenants: 2600 },
    { month: 'Jun', revenue: 810, users: 45200, tenants: 2750 },
    { month: 'Jul', revenue: 847, users: 45892, tenants: 2847 }
  ];

  // Mock notifications data
  const notificationsData = [
    {
      id: 1,
      type: 'critical',
      title: 'High Memory Usage Alert',
      message: 'Server cluster experiencing 85% memory utilization. Immediate attention required.',
      timestamp: new Date(Date.now() - 300000),
      read: false,
      action: 'View Details'
    },
    {
      id: 2,
      type: 'warning',
      title: 'Invoice Overdue',
      message: 'TechCorp Inc. has an overdue invoice of $12,500 (15 days past due).',
      timestamp: new Date(Date.now() - 900000),
      read: false,
      action: 'Send Reminder'
    },
    {
      id: 3,
      type: 'info',
      title: 'New Enterprise Signup',
      message: 'GlobalTech Solutions has signed up for Enterprise plan ($5,000/month).',
      timestamp: new Date(Date.now() - 1800000),
      read: true
    },
    {
      id: 4,
      type: 'success',
      title: 'Backup Completed',
      message: 'Daily database backup completed successfully. 2.3GB archived.',
      timestamp: new Date(Date.now() - 3600000),
      read: true
    },
    {
      id: 5,
      type: 'warning',
      title: 'Storage Limit Warning',
      message: 'DataFlow Corp is approaching 90% of their storage limit (450GB/500GB).',
      timestamp: new Date(Date.now() - 7200000),
      read: false,
      action: 'Upgrade Plan'
    },
    {
      id: 6,
      type: 'info',
      title: 'API Rate Limit Reached',
      message: 'CloudSync Ltd has reached 80% of their API rate limit for this billing cycle.',
      timestamp: new Date(Date.now() - 10800000),
      read: true
    }
  ];

  // Mock top tenants data
  const topTenantsData = [
    {
      id: 1,
      name: 'TechCorp Inc.',
      plan: 'Enterprise',
      revenue: 12500,
      revenueGrowth: 15.2,
      users: 2847,
      userGrowth: 142,
      usage: 87,
      status: 'active',
      renewalDate: '2025-09-15',
      renewalDays: 52
    },
    {
      id: 2,
      name: 'GlobalTech Solutions',
      plan: 'Enterprise',
      revenue: 8900,
      revenueGrowth: 22.8,
      users: 1923,
      userGrowth: 89,
      usage: 72,
      status: 'active',
      renewalDate: '2025-08-22',
      renewalDays: 28
    },
    {
      id: 3,
      name: 'DataFlow Corp',
      plan: 'Professional',
      revenue: 5600,
      revenueGrowth: 8.4,
      users: 1456,
      userGrowth: 67,
      usage: 94,
      status: 'active',
      renewalDate: '2025-08-05',
      renewalDays: 11
    },
    {
      id: 4,
      name: 'CloudSync Ltd',
      plan: 'Professional',
      revenue: 4200,
      revenueGrowth: 12.1,
      users: 987,
      userGrowth: 45,
      usage: 68,
      status: 'active',
      renewalDate: '2025-09-30',
      renewalDays: 67
    },
    {
      id: 5,
      name: 'InnovateTech',
      plan: 'Starter',
      revenue: 1800,
      revenueGrowth: 35.7,
      users: 234,
      userGrowth: 28,
      usage: 45,
      status: 'trial',
      renewalDate: '2025-08-01',
      renewalDays: 7
    },
    {
      id: 6,
      name: 'NextGen Systems',
      plan: 'Professional',
      revenue: 3400,
      revenueGrowth: 18.9,
      users: 678,
      userGrowth: 34,
      usage: 82,
      status: 'active',
      renewalDate: '2025-10-12',
      renewalDays: 79
    }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    // In real app, this would trigger data refetch
  };

  const handleTenantFilterChange = (filter) => {
    setTenantFilter(filter);
    // In real app, this would trigger data refetch
  };

  const handleRefreshIntervalChange = (interval) => {
    setRefreshInterval(interval);
    // In real app, this would set up auto-refresh
  };

  return (
    <>
      <Helmet>
        <title>Platform Overview Dashboard - SuperAdmin</title>
        <meta name="description" content="Comprehensive business intelligence and system monitoring for SaaS platform administrators" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar />
        
        <main className="lg:ml-60 pt-16 lg:pt-0">
          <div className="p-6 pb-20 lg:pb-6">
            <DashboardHeader
              onDateRangeChange={handleDateRangeChange}
              onTenantFilterChange={handleTenantFilterChange}
              onRefreshIntervalChange={handleRefreshIntervalChange}
            />
            
            {/* KPI Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {kpiData.map((kpi, index) => (
                <KPICard
                  key={index}
                  title={kpi.title}
                  value={kpi.value}
                  change={kpi.change}
                  changeType={kpi.changeType}
                  icon={kpi.icon}
                  trend={kpi.trend}
                  loading={loading}
                />
              ))}
            </div>
            
            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
              {/* Revenue Chart - Takes 2 columns on xl screens */}
              <div className="xl:col-span-2">
                <RevenueChart data={chartData} loading={loading} />
              </div>
              
              {/* Notifications Feed - Takes 1 column on xl screens */}
              <div className="xl:col-span-1">
                <NotificationFeed notifications={notificationsData} loading={loading} />
              </div>
            </div>
            
            {/* Top Tenants Table */}
            <div className="mb-8">
              <TopTenantsTable tenants={topTenantsData} loading={loading} />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default PlatformOverviewDashboard;